import { h, Component, createRef } from 'preact';
import StyledInviteManager from './styledInviteManager';
import { StyleSheetManager } from 'styled-components';
import Invite from '../components/Invite';
import MobileInvite from '../components/MobileInvite';
import postMessageManager from '../services/postMessageManager';
import {
  POST_MESSAGE_ACTIONS,
  INVITE_ACTIONS,
  INVITE_EVENTS,
  DEFAULT_LANGUAGE,
  PROVISIONS,
} from '../common/constants';
import { INVITE_PROP_TYPES, DEFAULT_INVITE_DATA } from './inviteStructure';
import { isMobileDevice } from '../common/utils';
import { getQueryParam } from '../services/urlHandler';
import { formatPostMessage } from '../common/utils';
import { tabManager } from '@mdigital/common/dist/utils/tabManager';
import {
  retrieveDomainsList,
  isOriginValid,
} from '@mdigital/common/dist/utils/domainsList';
import {
  waitForAssetsLoad,
  waitForOpenSansFontLoad,
} from '../helpers/assetsLoader';
import Spinner from './styled';

let mobileVerison = 1;
const SPINNER_TIMEOUT = 4000;
function renderAppropriateInvitation(
  isMobile,
  props,
  onProvideFeedbackClicked,
  onDeclineButtonClicked,
  onMobileInviteRendered,
  onDeferButtonClicked
) {
  return isMobile ? (
    <MobileInvite
      {...props}
      {...props.application.inviteData}
      {...props.application.inviteData.mobileDesign}
      {...props.application.inviteData.desktopDesign}
      onDeferButtonClicked={onDeferButtonClicked}
      onProvideFeedbackClicked={onProvideFeedbackClicked}
      onDeclineButtonClicked={onDeclineButtonClicked}
      onMobileInviteRendered={onMobileInviteRendered}
    />
  ) : (
    <Invite
      {...props}
      {...props.application.inviteData}
      {...props.application.inviteData.desktopDesign}
      onDeferButtonClicked={onDeferButtonClicked}
      onProvideFeedbackClicked={onProvideFeedbackClicked}
      onDeclineButtonClicked={onDeclineButtonClicked}
    />
  );
}

/**
 * copied styles from invite templates on kampyleappv5 repo,
 * used to be inside a style tag, which violated CSP policies
 */
function addLegacyStyle() {
  const htmlEl = document.getElementsByTagName('html')[0];
  const bodyEl = document.getElementsByTagName('body')[0];

  if (htmlEl instanceof HTMLElement && htmlEl.style) {
    htmlEl.style.setProperty('height', '100%');
    htmlEl.style.setProperty('-webkit-text-size-adjust', '100%');
    htmlEl.style.setProperty('text-size-adjust', '100%');

    if (window.CSS?.supports('font', '-apple-system-body')) {
      htmlEl.style.setProperty('font', '-apple-system-body');
    }
  }

  if (bodyEl instanceof HTMLElement && bodyEl.style) {
    bodyEl.style.setProperty('height', '100%');
    bodyEl.style.setProperty('line-height', '1.42857143');
    bodyEl.style.setProperty('margin', '0');
  }
}

class InviteManager extends Component {
  allowedDomainsList = undefined;
  constructor(props) {
    super(props);
    this.onProvideFeedbackClicked = this.onProvideFeedbackClicked.bind(this);
    this.onDeclineButtonClicked = this.onDeclineButtonClicked.bind(this);
    this.onMobileInviteRendered = this.onMobileInviteRendered.bind(this);
    this.onDeferButtonClicked = this.onDeferButtonClicked.bind(this);
    this.sendMessageToOnsite = this.sendMessageToOnsite.bind(this);
    this.getBackgroundColor = this.getBackgroundColor.bind(this);
    this.handleLogoLoaded = this.handleLogoLoaded.bind(this);
    this.renderCustomCss = this.renderCustomCss.bind(this);
    this.domNode = document.getElementsByTagName('body')[0];
    this.state = {
      application: null,
      tabManagerLoaded: false,
      isLoadingCustomCss: false,
      isLoadingLogo: false,
      isLoadingMobileShortInvite: true,
      onImageLoad: null,
    };
    this.wrapperRef = createRef();
    this.tabManagerService = tabManager();
  }

  componentDidMount() {
    addLegacyStyle();
    window.addEventListener('keydown', e => {
      if (e.which && e.which === 27) {
        this.onDeferButtonClicked();
      }
    });
    const domainsListPath = getQueryParam('domainsListRelativePath');
    retrieveDomainsList({
      domainsListPath,
      onSuccess: data => {
        window.addEventListener(INVITE_EVENTS.MESSAGE, ev =>
          this.handlePostMessage(ev)
        );
        this.allowedDomainsList = data;
        postMessageManager.sendMessage(POST_MESSAGE_ACTIONS.INVITE_LOADED, {
          inviteVersion: mobileVerison,
          formId: getQueryParam('formId'),
        });
      },
    });
  }

  componentDidUpdate() {
    const isMobileSmallInvite =
      this.state.application &&
      isMobileDevice(this.state.application.deviceType) &&
      this.state.application.provisions.mobileInvitationLayout &&
      this.state.application.inviteData.mobileDesign;
    if (
      !isMobileSmallInvite &&
      !!this.wrapperRef.current &&
      !this.state.tabManagerLoaded
    ) {
      this.enableTabManager();
    }
  }

  onMobileInviteRendered() {
    if (!this.state.tabManagerLoaded) {
      waitForOpenSansFontLoad(() => {
        this.setState({ isLoadingMobileShortInvite: false });
        this.enableTabManager();
      });
    }
  }

  enableTabManager() {
    this.tabManagerService.setTabLoop(this.wrapperRef.current);
    this.wrapperRef?.current?.focus();
    this.setState({
      tabManagerLoaded: true,
    });
  }

  removeTabManager() {
    if (this.state.tabManagerLoaded) {
      this.tabManagerService.removeTabLoop();
      this.setState({
        tabManagerLoaded: false,
      });
    }
  }

  reportInvalidOrigin = origin => {
    postMessageManager.sendMessage(INVITE_ACTIONS.INVALID_ORIGIN, {
      formId: getQueryParam('formId'),
      invalidOrigin: origin,
    });
  };

  handlePostMessage(ev) {
    const postMessage = formatPostMessage(ev.data);
    if (!postMessage.__isDigitalAction) {
      return;
    }
    if (!isOriginValid(ev.origin, this.allowedDomainsList)) {
      this.reportInvalidOrigin(ev.origin);
      return;
    }
    switch (postMessage.action) {
      case POST_MESSAGE_ACTIONS.SET_EXTRA_DATA:
        this.setState({ application: postMessage.data }, _ => {
          this.setHtmlLanguageByData();
          this.setHtmlTitleElementByData();
          const shouldRenderCustomCss = !!(
            this.state.application.inviteData.customCssUrl ||
            this.state.application.inviteData.desktopDesign
              ?.customCssFileResource?.url
          );
          shouldRenderCustomCss && this.renderCustomCss();
          const shouldWaitForLogo = !!(
            this.state.application.inviteData.desktopDesign?.logoUrl?.url &&
            this.state.application.provisions[
              PROVISIONS.ENABLE_FORM_V2_AND_INVITE_CSS_Delay
            ]
          );
          if (shouldWaitForLogo) {
            this.setState({
              onImageLoad: this.handleLogoLoaded,
              isLoadingLogo: true,
            });
            this.restrictSpinner();
          }
        });
        break;
      case POST_MESSAGE_ACTIONS.SET_FOCUS:
        // eslint-disable-next-line no-case-declarations
        const inviteTitleElement =
          document.getElementsByClassName('invitation-title')[0];
        if (inviteTitleElement) {
          inviteTitleElement.tabIndex = -1;
          if (document.activeElement !== inviteTitleElement) {
            inviteTitleElement.focus();
          }
        }
        break;
      default:
        break;
    }
  }

  onProvideFeedbackClicked(e) {
    e.preventDefault();
    e.stopPropagation();

    this.removeTabManager();
    this.sendMessageToOnsite(INVITE_ACTIONS.INVITE_ACCEPTED);
  }

  onDeclineButtonClicked() {
    this.removeTabManager();
    this.sendMessageToOnsite(INVITE_ACTIONS.INVITE_DECLINED);
  }

  onDeferButtonClicked() {
    this.removeTabManager();
    this.sendMessageToOnsite(INVITE_ACTIONS.INVITE_DEFERED);
  }

  sendMessageToOnsite(event) {
    postMessageManager.sendMessage(event, {
      formId: this.state.application.formId,
    });
  }

  setHtmlLanguageByData() {
    if (this.state.application && this.state.application.isWCAG) {
      const currentLanguage =
        this.state.application.formLanguage || DEFAULT_LANGUAGE;
      document
        .getElementsByTagName('html')[0]
        .setAttribute('lang', currentLanguage);
    }
  }

  setHtmlTitleElementByData() {
    const titleElement =
      this.state.application?.inviteData?.desktopDesign?.titleElement;
    if (titleElement) {
      document.title = titleElement;
    }
  }

  getBackgroundColor(isMobile) {
    const inviteData = this.state.application.inviteData;
    if (isMobile && inviteData.mobileDesign) {
      return inviteData.mobileDesign.invitationBackgroundColor;
    } else if (inviteData.desktopDesign) {
      return inviteData.desktopDesign.invitationBackgroundColor;
    }
    return inviteData.invitationBackgroundColor;
  }

  renderCustomCss() {
    const isDelayEnabled =
      this.state.application.provisions[
        PROVISIONS.ENABLE_FORM_V2_AND_INVITE_CSS_Delay
      ];
    if (isDelayEnabled) {
      this.setState({ isLoadingCustomCss: true });
      this.restrictSpinner();
    }
    const linkElem = document.createElement('link');
    linkElem.href =
      this.state.application.inviteData.customCssUrl ||
      this.state.application.inviteData.desktopDesign?.customCssFileResource
        ?.url;
    linkElem.rel = 'stylesheet';
    linkElem.property = 'stylesheet';
    linkElem.onload = () => {
      if (isDelayEnabled) {
        waitForAssetsLoad(() => this.setState({ isLoadingCustomCss: false }));
      }
    };
    linkElem.onerror = () => {
      this.setState({ isLoadingCustomCss: false });
    };
    this.domNode.appendChild(linkElem);
  }

  handleLogoLoaded() {
    this.setState({ isLoadingLogo: false });
  }

  restrictSpinner() {
    const showInviteAnywayTimeout = setTimeout(() => {
      this.setState({
        isLoadingCustomCss: false,
        isLoadingLogo: false,
      });
    }, SPINNER_TIMEOUT);
    return () => clearTimeout(showInviteAnywayTimeout);
  }

  render() {
    if (
      !this.state.application ||
      !this.state.application.inviteData ||
      !this.state.application.provisions
    ) {
      return null;
    }
    const isMobile = isMobileDevice(this.state.application.deviceType);
    const invitationBackgroundColor = this.getBackgroundColor(isMobile);
    const isMobileSmallInvite = isMobile
      && this.state.application.provisions.mobileInvitationLayout
      && this.state.application.inviteData.mobileDesign;
    const isWithInviteReflow = this.state.application.provisions[PROVISIONS.ENABLE_INVITE_REFLOW];
      return (
        <StyleSheetManager shouldForwardProp={() => true}>
        <StyledInviteManager
          className="neb-invite-container"
          backgroundColor={invitationBackgroundColor}
          isMobileSmallInvite={isMobileSmallInvite}
          isLoadingMobileShortInvite={this.state.isLoadingMobileShortInvite}
          isRTL={this.state.application.inviteData.isRtl}
          isInviteReflow={isWithInviteReflow}
          ref={this.wrapperRef}
          tabindex="-1"
        >
          {!isMobileSmallInvite &&
          (this.state.isLoadingCustomCss || this.state.isLoadingLogo) ? (
            <Spinner />
          ) : null}
          {renderAppropriateInvitation(
            isMobile,
            this.state,
            this.onProvideFeedbackClicked,
            this.onDeclineButtonClicked,
            this.onMobileInviteRendered,
            this.onDeferButtonClicked
          )}
        </StyledInviteManager>
      </StyleSheetManager>
    );
  }
}

InviteManager.propTypes = INVITE_PROP_TYPES;
InviteManager.defaultProps = DEFAULT_INVITE_DATA;

export default InviteManager;
